import client, { toInventoryList, encodeInventoryQueryParams } from './inventory-request';
import { resolvePharmacyScope } from '../hooks/usePharmacyRole';
import {
  FALLBACK_PRESCRIPTIONS,
  FALLBACK_BILLS,
  fallbackPrescriptionDetail,
  isPharmacyNotFound,
  isPrescriptionFeedMissing,
  withListFallback,
} from '../utils/pharmacyFallbacks';
import {
  isActivePrescriptionStatus,
  normalizePrescriptionStatus,
  mapPrescribeMedicine,
  mapPrescription,
  prepareBrandSearchQuery,
  prepareGenericSearchTerm,
} from '../utils/pharmacyMappers';
import {
  OPD_LOOKUP_ERROR,
  buildPrescriptionLookupBody,
  formatOpdPrescriptionError,
  lookupValidationMessage,
} from '../utils/opdPrescriptionEntity';

let cachedIpdPrescriptions = null;

/** Normalize medicine lines from nested `details` or flat demo shape. */
function normalizeIpdMedicine(m) {
  if (!m || typeof m !== 'object') return m;
  const details = m.details && typeof m.details === 'object' ? m.details : {};
  return {
    ...m,
    name: m.name || m.medicine_name || m.item_name || '',
    medicine_name: m.medicine_name || m.name || '',
    generic_name: m.generic_name || m.generic_medicine_names || m.generic_medicine_name || '',
    dose: m.dose ?? details.dose ?? '',
    duration: m.duration ?? details.duration ?? '',
    frequency: m.frequency ?? details.frequency ?? details.frequency_code ?? '',
    stat: m.stat ?? details.stat ?? false,
  };
}

/**
 * API returns patients with nested `prescriptions[]` (keyed by `order_no`).
 * Older/demo payloads are already flat prescriptions with `id`.
 */
function flattenIpdPrescriptions(payload) {
  const patients = Array.isArray(payload) ? payload : toInventoryList(payload);
  const rows = [];

  patients.forEach((patient) => {
    if (!patient || typeof patient !== 'object') return;
    const nested = Array.isArray(patient.prescriptions) ? patient.prescriptions : null;

    if (!nested) {
      // Flat prescription row (demo / legacy)
      const orderNo = patient.order_no || patient.prescription_number || patient.order_number;
      const id = patient.id || patient.prescription_id || orderNo;
      rows.push({
        ...patient,
        id,
        prescription_id: patient.prescription_id || id,
        prescription_number: patient.prescription_number || orderNo || id,
        medicines: (Array.isArray(patient.medicines) ? patient.medicines : []).map(normalizeIpdMedicine),
      });
      return;
    }

    nested.forEach((rx) => {
      if (!rx || typeof rx !== 'object') return;
      const orderNo = rx.order_no || rx.prescription_number || rx.order_number || '';
      const id = rx.id || rx.prescription_id || orderNo;
      rows.push({
        patient_id: patient.patient_id,
        admission_id: patient.admission_id,
        uhid: patient.uhid,
        name: patient.name,
        email: patient.email,
        address_line: patient.address_line,
        phone_no: patient.phone_no,
        aadhaar_no: patient.aadhaar_no,
        abha_id: patient.abha_id,
        abha_no: patient.abha_no,
        day_of_birth: patient.day_of_birth,
        gender: patient.gender,
        id,
        prescription_id: id,
        order_no: orderNo,
        prescription_number: orderNo || id,
        order_number: orderNo || id,
        doctor_id: rx.doctor_id,
        doctor_name: rx.doctor_name,
        provisional_diagnosis: rx.provisional_diagnosis,
        final_diagnosis: rx.final_diagnosis,
        allergies: Array.isArray(rx.allergies) ? rx.allergies : [],
        status: rx.status,
        bed_no: rx.bed_no,
        room_no: rx.room_no,
        nurse_id: rx.nurse_id,
        nurse_name: rx.nurse_name,
        assigned_by: rx.assigned_by,
        ipd_flag: rx.ipd_flag,
        created_at: rx.created_at,
        updated_at: rx.updated_at,
        medicines: (Array.isArray(rx.medicines) ? rx.medicines : []).map(normalizeIpdMedicine),
      });
    });
  });

  return rows;
}

async function fetchIpdPrescriptions() {
  const result = await client.get('/get_ipd_prescriptions/');
  const list = flattenIpdPrescriptions(result);
  cachedIpdPrescriptions = list;
  return list;
}

function filterPrescriptions(list, params = {}) {
  if (!params?.status) return list;
  return list.filter((row) => {
    if (params.status === 'ACTIVE') return isActivePrescriptionStatus(row.status);
    return normalizePrescriptionStatus(row.status) === params.status;
  });
}

/** Resolves via the same id chain mapPrescription uses, so lookups agree with what the table navigates with. */
function findPrescriptionById(id) {
  if (!cachedIpdPrescriptions || id == null || id === '') return null;
  const target = String(id);
  return cachedIpdPrescriptions.find((row, index) => (
    String(mapPrescription(row, index).id) === target
    || String(row.id) === target
    || String(row.prescription_id || '') === target
    || String(row.order_no || '') === target
    || String(row.prescription_number || '') === target
    || String(row.order_number || '') === target
  )) || null;
}

// Order-book status transition (CPOE pipeline). body: {admission_id, status,
// order_no? | prescription_ids?, reason?}. Forward-only Sent -> Acknowledged ->
// Dispensed -> Received (+Cancel); rejected rows ride in the envelope's
// `rejected` — a repeat transition is a no-op there, not an error, so
// best-effort callers can fire-and-forget.
// API: POST /update_ipd_prescription_status/
export const updateIpdPrescriptionStatus = (body) =>
  client.post('/update_ipd_prescription_status/', body);

// IPD prescriptions — medicines and allergies are embedded in each list item
// API: GET /get_ipd_prescriptions/
// Only a 404 seeds demo data (isPrescriptionFeedMissing, not the module-wide
// isPharmacyNotFound): a 403/500/network failure has to reach the screen, not be
// masked by fake orders the pharmacist could act on.
export async function getPrescriptions(params) {
  try {
    const list = filterPrescriptions(await fetchIpdPrescriptions(), params);
    return withListFallback(list, FALLBACK_PRESCRIPTIONS);
  } catch (err) {
    if (isPrescriptionFeedMissing(err)) return withListFallback([], FALLBACK_PRESCRIPTIONS);
    throw err;
  }
}

/** Detail is resolved from the cached IPD list (no separate detail endpoint). */
export async function getPrescription(id) {
  try {
    if (!cachedIpdPrescriptions) await fetchIpdPrescriptions();
    const found = findPrescriptionById(id);
    if (found) return found;
    const fallback = fallbackPrescriptionDetail(id);
    if (fallback) return fallback;
    throw new Error('Prescription not found');
  } catch (err) {
    const fallback = fallbackPrescriptionDetail(id);
    if (fallback) return fallback;
    throw err;
  }
}

// Nurses currently assigned to a bed, for the Deliver step's handover field.
// Reads the EMR's own nurse_bed_assignment/ (same backend as every other call
// here — inventory-request already sends the APP-MODE / ORG-CODE /
// USER-ACCESS-KEY / USERNAME headers check_user_access needs, and that
// endpoint has no role gate, so a pharmacist login resolves it fine).
// Never throws: an unreachable roster must not block a delivery, so the
// caller just falls back to the nurse stamped on the prescription.
// API: GET /nurse_dashboard/nurse_bed_assignment/?bed_code=&is_active=true
export async function getBedNurses(bedCode) {
  const code = String(bedCode ?? '').trim();
  if (!code) return [];
  try {
    const res = await client.get('/nurse_dashboard/nurse_bed_assignment/', {
      bed_code: code,
      is_active: 'true',
    });
    return toInventoryList(res);
  } catch {
    return [];
  }
}

/** Failure carrying a user-facing `message` and a stable `code` from OPD_LOOKUP_ERROR. */
function opdLookupError(code, message, cause) {
  const error = new Error(message);
  error.name = 'OpdPrescriptionLookupError';
  error.code = code;
  if (cause) {
    error.status = cause.status ?? cause.response?.status;
    error.cause = cause;
  }
  return error;
}

// OPD prescription history for one patient, looked up by phone or ABHA address.
// Takes the raw search string — discriminating phone vs ABHA is a request-shaping
// concern, so it lives here rather than in the screen. Always resolves to
// `{ patients }` with an array (empty is a valid result, not an error); every
// failure rejects with an already-user-facing message.
// API: POST /get_patient_prescription_details/  body: {phone_no|abha_address, from_date, to_date}
export async function getPatientPrescriptionDetails(query) {
  const body = buildPrescriptionLookupBody(query);
  if (!body) {
    throw opdLookupError(OPD_LOOKUP_ERROR.INVALID_QUERY, lookupValidationMessage());
  }

  try {
    const data = await client.post('/get_patient_prescription_details/', body);
    const patients = Array.isArray(data?.patient_prescriptions) ? data.patient_prescriptions : [];
    return { patients };
  } catch (err) {
    throw opdLookupError(OPD_LOOKUP_ERROR.REQUEST_FAILED, formatOpdPrescriptionError(err), err);
  }
}

// Controlled substance queue
// API: GET /inventory/pharmacy/dispensing/controlled-auth/

function controlledAuthScopeParams(p = {}) {
  const params = {};
  if (p.tenant_id) params.tenant_id = p.tenant_id;
  if (p.hospital_id) params.hospital_id = p.hospital_id;
  if (p.status) params.status = p.status;
  return Object.keys(params).length ? params : undefined;
}

/**
 * Controlled queue. No demo fallback: a fabricated row here reads as a real
 * pending authorization, and an empty queue is the normal, correct state.
 * Pass inventory_transaction_id to poll one sale's own lines instead of the
 * whole facility queue.
 */
export const getControlledQueue = (p = {}) =>
  client.get('/inventory/pharmacy/dispensing/controlled-auth/', { status: 'PENDING', ...p });

/** Unfiltered list — no mock fallback (used to poll Direct Dispense auth status). */
export const listControlledAuth = (p = {}) =>
  client.get('/inventory/pharmacy/dispensing/controlled-auth/', controlledAuthScopeParams(p));

// API: POST /inventory/pharmacy/dispensing/controlled-auth/
export const createControlledAuth = (body, scope = {}) =>
  client.post('/inventory/pharmacy/dispensing/controlled-auth/', body, controlledAuthScopeParams(scope));

// API: POST /inventory/pharmacy/dispensing/controlled-auth/{auth_id}/authorize/
export const authorizeControlledItem = (authId, body = {}, scope = {}) =>
  client.post(
    `/inventory/pharmacy/dispensing/controlled-auth/${authId}/authorize/`,
    body,
    controlledAuthScopeParams(scope),
  );

// API: POST /inventory/pharmacy/dispensing/controlled-auth/{auth_id}/deny/
export const denyControlledItem = (authId, body = {}, scope = {}) =>
  client.post(
    `/inventory/pharmacy/dispensing/controlled-auth/${authId}/deny/`,
    body,
    controlledAuthScopeParams(scope),
  );

/** @deprecated use denyControlledItem */
export const rejectControlledItem = denyControlledItem;

// Pharmacy billing records
// API: GET /inventory/pharmacy/bills/
export const getPharmacyBills = (p) =>
  listWithFallback('/inventory/pharmacy/bills/', p, FALLBACK_BILLS);
// API: POST /inventory/pharmacy/bills/
export const createPharmacyBill = (body) => client.post('/inventory/pharmacy/bills/', body);

// Insurance pre-authorization
// API: GET /inventory/pharmacy/pre-auth/
export const getPreAuthRequests = (p) => client.get('/inventory/pharmacy/pre-auth/', p);
// API: PATCH /inventory/pharmacy/pre-auth/{id}/
export const updatePreAuth = (id, body) => client.patch(`/inventory/pharmacy/pre-auth/${id}/`, body);

// Ward returns — pharmacy inbox (acknowledge/reject lifecycle)
const WARD_RETURNS_BASE = '/inventory/pharmacy/ward-returns';

/** Pharmacy return types that belong in this inbox (Patient Return creates PATIENT_RETURN). */
const PHARMACY_INBOX_RETURN_TYPES = new Set([
  'WARD_RETURN',
  'PATIENT_RETURN',
  'DEPARTMENT_RETURN',
  'PHARMACY_RETURN',
]);

function isPharmacyInboxReturn(row) {
  const returnType = row?.transaction_payload?.return_type;
  // Missing return_type: keep row (older payloads); known non-pharmacy types drop out.
  if (!returnType) return true;
  return PHARMACY_INBOX_RETURN_TYPES.has(returnType);
}

/**
 * List inventory returns for the pharmacy inbox.
 * Server has no return_type filter — keep pharmacy return types only.
 * Prefer limit=200 — client filter after paging under-fills smaller pages.
 * `hasMore` is based on the unfiltered page length (true total is unknown).
 */
export async function getWardReturns(params = {}) {
  const limit = Number(params.limit) > 0 ? Number(params.limit) : 200;
  const data = await client.get(`${WARD_RETURNS_BASE}/`, { ...params, limit });
  const rawItems = Array.isArray(data) ? data : (data?.items ?? []);
  const items = rawItems.filter(isPharmacyInboxReturn);
  const hasMore = rawItems.length >= limit;
  if (Array.isArray(data)) return { items, total: items.length, limit, offset: 0, hasMore };
  return { ...data, items, total: items.length, hasMore };
}

/** API: GET /inventory/pharmacy/ward-returns/{id}/ */
export const getWardReturn = (id) => client.get(`${WARD_RETURNS_BASE}/${id}/`);

/** API: GET /inventory/pharmacy/ward-returns/{id}/items/ — plain array */
export const getWardReturnItems = (id) => client.get(`${WARD_RETURNS_BASE}/${id}/items/`);

/**
 * Pharmacy accept/partial/reject-by-line. Idempotent — safe to retry on network failure.
 * Always send explicit line_items covering every line.
 * API: POST /inventory/pharmacy/ward-returns/{id}/acknowledge/
 */
export const acknowledgeWardReturn = (id, body) =>
  client.post(`${WARD_RETURNS_BASE}/${id}/acknowledge/`, body);

/**
 * Blanket refuse. NOT idempotent — do not auto-retry.
 * API: POST /inventory/pharmacy/ward-returns/{id}/reject/
 */
export const rejectWardReturn = (id, body) =>
  client.post(`${WARD_RETURNS_BASE}/${id}/reject/`, body);

async function listWithFallback(path, params, seedRows) {
  try {
    const result = await client.get(path, params);
    return withListFallback(result, seedRows);
  } catch (err) {
    if (isPharmacyNotFound(err)) return withListFallback([], seedRows);
    throw err;
  }
}

const PRESCRIBE_SEARCH_LIMIT = 50;
const AVAILABILITY_SEARCH_LIMIT = 20;
const PRESCRIBE_SEARCH_PATH = '/inventory/pharmacy/prescribe/search/';

function parsePrescribeSearchResponse(data, limit, offset) {
  const items = (Array.isArray(data) ? data : toInventoryList(data)).map(mapPrescribeMedicine);
  const totalItems = !Array.isArray(data) && data && typeof data === 'object'
    ? Number(data.total_items ?? data.total)
    : NaN;
  const hasMore = Number.isFinite(totalItems) && totalItems > 0
    ? offset + items.length < totalItems
    : items.length >= limit;
  return { items, hasMore, limit, offset };
}

/**
 * Per-medicine stock check for the Start Dispense flow — search by brand (`q`)
 * or generic name (`generic_name`) at the selected pharmacy store.
 * API: GET /inventory/pharmacy/prescribe/search/
 * Params: tenant_id, hospital_id, q, generic_name, dosage_form, available_only, location_id
 */
export async function searchPrescribeAvailability(params = {}) {
  const {
    tenant_id,
    hospital_id,
    q,
    generic_name,
    dosage_form,
    available_only,
    location_id,
    limit = AVAILABILITY_SEARCH_LIMIT,
    offset = 0,
  } = params;

  if (!q && !generic_name) {
    return { items: [], hasMore: false, limit, offset };
  }

  const brandQuery = q ? prepareBrandSearchQuery(q) : '';
  const genericQuery = generic_name ? prepareGenericSearchTerm(generic_name) : '';
  if (!brandQuery && !genericQuery) {
    return { items: [], hasMore: false, limit, offset };
  }

  const query = { limit, offset };
  if (brandQuery) query.q = brandQuery;
  if (genericQuery) query.generic_name = genericQuery;
  if (tenant_id) query.tenant_id = tenant_id;
  if (hospital_id) query.hospital_id = hospital_id;
  if (dosage_form) query.dosage_form = dosage_form;
  if (available_only) query.available_only = available_only;
  if (location_id) query.location_id = location_id;

  const data = await client.get('/inventory/pharmacy/prescribe/search/', query, {
    paramsSerializer: { serialize: encodeInventoryQueryParams },
  });
  const items = (Array.isArray(data) ? data : toInventoryList(data)).map(mapPrescribeMedicine);
  return {
    items,
    hasMore: items.length >= limit,
    limit,
    offset,
  };
}

/**
 * Medicine catalogue for pharmacy direct dispense (batches, MRP, sale price).
 * API: GET /inventory/pharmacy/prescribe/search/?tenant_id=&hospital_id=&location_id=
 * `location_id` is optional — walk-in / catalogue search can omit it (same as IPD prescribe search).
 */
export async function searchPrescribeMedicines(params = {}) {
  const {
    store,
    store_id,
    tenant_id,
    hospital_id,
    location_id,
    q,
    generic_name,
    limit = PRESCRIBE_SEARCH_LIMIT,
    offset = 0,
  } = params;

  const scope = resolvePharmacyScope(store, { tenant_id, hospital_id });
  if (!scope.tenant_id || !scope.hospital_id) {
    return { items: [], hasMore: false, limit, offset };
  }

  const query = {
    tenant_id: scope.tenant_id,
    hospital_id: scope.hospital_id,
    limit,
    offset,
  };
  const locationId = location_id || store_id;
  if (locationId) query.location_id = locationId;
  const nameFilter = generic_name?.trim();
  if (nameFilter) query.generic_name = nameFilter;
  const qFilter = q?.trim();
  if (qFilter) query.q = qFilter;

  const data = await client.get(PRESCRIBE_SEARCH_PATH, query);
  return parsePrescribeSearchResponse(data, limit, offset);
}
